/* eslint-disable no-nested-ternary */

import _, { get } from 'lodash';
import moment from 'moment';
import SimpleFormikField from 'novigo-simple-formik-field';
import React from 'react';
import { Button, Grid, Popup, Icon, Checkbox } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from 'react-router-dom';
import FormikAsyncSelect from '../../components/FormikAsyncSelect';
import FormikDate from '../../components/FormikDate';
import { PaddedColumn } from '../../components/Layout';
import DestinationService from '../../shared/services/destination';
import OrderService from '../../shared/services/order';
import OrderTypologyService from '../../shared/services/ordertypology';
import AgentService from '../../shared/services/agent';
import appMessages from '../App/messages';
import messages from './messages';
import PermissionManager from '../../components/Permission';
import ProductService from '../../shared/services/product';
import FormikInput from '../../components/FormikInput';
import { RemoveBtn } from '../../components/Buttons';
import { getNetPrice } from '../../components/Forms/OrderForm/manageForm/functions';
import { applyMerchDiscount } from './functions';
import currencyFormatter from "../../utils/currencyFormatter";

export const DEFAULT_ACTION = 'app/Order/DEFAULT_ACTION';

export const ORDER_STATE_INSERTED = 'Inserito';
export const ORDER_STATE_TO_BE_ACCEPTED = 'Da Autorizzare';
export const ORDER_STATE_ACCEPTED = 'Approvato';
export const ORDER_STATE_NOT_ACCEPTED = 'Non Approvato';
export const ORDER_STATE_SUSPENDED = 'Sospeso';
export const ORDER_STATE_ALL = 'TUTTI';

export const ORDER_STATES = [
  ORDER_STATE_INSERTED,
  ORDER_STATE_TO_BE_ACCEPTED,
  ORDER_STATE_ACCEPTED,
  ORDER_STATE_NOT_ACCEPTED,
  ORDER_STATE_SUSPENDED,
];

export const RELATED_TABLES = [
  {
    type: 'leftjoin',
    field: 'destination',
    alias: 'destination',
  },
  {
    type: 'leftjoin',
    field: 'orderType',
    alias: 'orderType',
  },
  { type: 'eq', field: 'finalized', value: 1 },
  { type: 'eq', field: 'state', value: ORDER_STATE_ACCEPTED },
];

export const OrderStateColorMap = {
  [ORDER_STATE_ACCEPTED]: 'green',
  [ORDER_STATE_SUSPENDED]: 'olive',
  [ORDER_STATE_INSERTED]: 'yellow',
  [ORDER_STATE_TO_BE_ACCEPTED]: 'yellow',
  [ORDER_STATE_NOT_ACCEPTED]: 'yellow',
};

const NetPriceEdited = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  span:nth-child(2) {
    font-size: 8pt;
    color: darkgrey;
  }
`;
/**
 * Returns the list of columns to be shown in the orders table.
 * @param {*} intl
 * @param {*} manageAction
 * @returns
 */
export const OrderColumns = (intl, manageAction) => [
  {
    key: 'number',
    name: intl.formatMessage(messages.orderNumber),
    searchable: true,
    sortable: true,
    width: 1,
    className: 'row-not-compliant',
    formatter: ({ data }) => {
      const number = data.documentNumber || data.number || '--';
      return number;
    },
  },
  {
    key: 'code',
    name: intl.formatMessage(messages.customer),
    useAlias: 'destination',
    searchable: true,
    sortable: true,
    formatter: data => get(data, 'data._embedded.destination.code', '--'),
    exportFormatter: data => get(data, 'data._embedded.destination.code', '--'),
  },
  {
    key: 'businessName',
    name: intl.formatMessage(messages.businessName),
    useAlias: 'destination',
    searchable: true,
    sortable: true,
    width: 400,
    className: 'row-not-compliant',
    formatter: data => {
      const insegna = get(
        data,
        'data._embedded.destination.businessName',
        '--',
      );
      const businessName = get(
        data,
        'data._embedded.customer.businessName',
        '--',
      );
      const { internalNote, delivery, state } = data.data;
      const stateColor = OrderStateColorMap[state] || 'grey';

      return (
        <div
          style={{
            height: '100%',
            display: 'flex',
            position: 'relative',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              background: `${stateColor}`,
              width: '4px',
              borderRadius: '2px',
              height: 'calc(100% + 8px)',
              position: 'absolute',
              top: '-4px',
            }}
          />
          <div style={{ marginLeft: '1rem' }}>
            {insegna}
            {delivery && (
              <>
                <br />
                <b>{intl.formatMessage(messages.destinationDelivery)}</b>
                {delivery}
              </>
            )}
            {internalNote && (
              <Popup
                inverted
                trigger={
                  <div className="note">
                    <b>{intl.formatMessage(messages.orderNote)}</b>
                    {internalNote}
                  </div>
                }
                content={internalNote}
                position="bottom left"
                style={{ borderRadius: '6px' }}
              />
            )}
          </div>
          <div style={{ marginLeft: '1rem' }}>
            {businessName}
          </div>
        </div>
      );
    },
    exportFormatter: data =>
      get(data, 'data._embedded.destination.businessName', '--'),
  },
  {
    key: 'agent',
    name: 'Agente',
    formatter: ({ data }) => {
      const agent = get(data, '_embedded.destination.agent1', null);
      return agent ? `${agent.displayName} (${agent.code})` : '--';
    },
    exportFormatter: ({ data }) => {
      const agent = get(data, '_embedded.destination.agent1', null);
      return agent ? `${agent.displayName} (${agent.code})` : '--';
    },
    searchable: false,
    sortable: false,
  },
  {
    key: 'referenceDate',
    name: intl.formatMessage(messages.orderDate),
    formatter: ({ data }) => {
      const { referenceDate } = data;
      const formattedReferenceDate = referenceDate
        ? moment(referenceDate).format('DD/MM/YYYY')
        : '--';
      return formattedReferenceDate;
    },
    searchable: true,
    sortable: true,
  },
  {
    key: 'referenceTime',
    name: 'Ora',
    formatter: ({ data }) => {
      const { referenceDate } = data;

      if (!referenceDate) {
        return '--';
      }

      const date = moment.utc(referenceDate, "YYYY-MM-DD HH:mm:ss").local();

      // Format hours, minutes, and seconds with leading zeros if necessary
      const formattedTime = date.format('HH:mm:ss');

      return formattedTime;
    },
    searchable: true,
    sortable: true,
  },
  {
    key: 'totalPriceAmount',
    formatter: ({ data }) => {
      const totalPriceAmount = data.totalPriceAmount || '0.00';
      return currencyFormatter.format(totalPriceAmount);
    },
    name: intl.formatMessage(messages.totalPriceAmount),
    searchable: true,
    sortable: true,
  },
  {
    key: 'avarageDiscount',
    className: "row-not-compliant",
    formatter: ({ data }) => {
      const averageDiscount = data.avarageDiscount || '0.00';
      return `${parseFloat(averageDiscount).toFixed(2)  } %`;
    },
    name: intl.formatMessage(messages.averageDiscount),
    searchable: true,
    sortable: true,
  },
  {
    key: 'maxDiscount',
    className: "row-not-compliant",
    formatter: ({ data }) => {
      const maxDiscount = data.maxDiscount || '0.00';
      return `${maxDiscount  } %`;
    },
    name: intl.formatMessage(messages.maxDiscount),
    searchable: true,
    sortable: true,
  },
  {
    key: 'actions',
    name: intl.formatMessage(messages.actions),
    searchable: false,
    sortable: false,
    formatter: ({ data }) => OrderActions(manageAction, data),
  },
];

/**
 * Returns the list of columns to be shown in the order rows table.
 * @param {*} intl
 * @param {*} onChangeRowData
 * @param {*} onAction
 * @param {*} isEdit
 * @returns
 */
export const OrderRowColumns = (
  intl,
  onChangeRowData,
  onAction,
  isEdit,
  handleOnArticleRowChange,
) => [
  // Product code
  {
    key: 'view',
    name: '',
    width: "0.5%",
    formatter: ({ data }) => {
      const replacementProducts = get(
        data,
        'product.replacementProducts',
        null,
      )
        ? get(data, 'product.replacementProducts', null)
          .map(p => p.title)
          .toString()
        : '--';

      const bought = get(data, 'bought', null);
      const isDuplicated = get(data, 'isDuplicated', null);

      const dataHover = [
        {
          key: 'Nota offerta',
          value: get(data, 'product.noteOffer', '--') || '--',
        },
        {
          key: 'Prezzo acquisto',
          value: get(data, 'buyPrice', null)
            ? currencyFormatter.format(get(data, 'buyPrice', null))
            : '--',
        },
        {
          key: 'Nota articolo',
          value: get(data, 'product.note', '--') || '--',
        },
        {
          key: 'Prodotto sostitutivo',
          value: replacementProducts,
        },
        {
          key: 'Informazioni',
          value: get(data, 'product.information', '--') || '--',
        },
      ];

      return (
            <>
              <Popup
                on='click'
                wide='very'
                position='right center'
                className='popup-container border'
                pinned
                content={
                  <>
                    <Link
                      to={`/product/${data.product.value}/view`}
                      style={{ color: 'black', textDecorationLine: 'underline' }}
                    >
                      <h3>
                        {get(data, 'product.productCode', '--')} -{' '}
                        {_.get(data, 'product.title', '')}
                      </h3>
                    </Link>
                    <div className="col">
                      {dataHover.map(row => (
                        <div style={row.style ? row.style : {}}>
                          {row.key}: {row.value}
                        </div>
                      ))}
                    </div>
                  </>
                }
                trigger={
                  <div className="popup-eye">
                    <Icon name="eye" size='large' />
                  </div>
                }
              />
              {bought &&
                <Popup
                  on='hover'
                  wide='very'
                  position='right center'
                  className='popup-container border'
                  pinned
                  content={
                    <>
                      <div className="col">
                          Articolo acquistato negli ultimi 2 anni.
                      </div>
                    </>
                  }
                  trigger={
                    <div className="popup-eye">
                      <Icon name="cart" size='large' />
                    </div>
                  }
                />
              }
              {isDuplicated &&
                <Popup
                  on='hover'
                  wide='very'
                  position='right center'
                  className='popup-container border'
                  pinned
                  content={
                    <>
                      <div className="col">
                        <p style={{ fontWeight: '600' }}>
                          Articolo duplicato
                        </p>
                        <p>
                          L&#39;articolo {get(data, 'product.productCode', '--')} è presente in più righe.
                        </p>
                      </div>
                    </>
                  }
                  trigger={
                    <div className="popup-eye">
                      <Icon name="copy" size='large' />
                    </div>
                  }
                />
              }
            </>
      )
    },
  },
  {
    key: 'product.productCode',
    name: isEdit
      ? `${intl.formatMessage(messages.productCode)} / ${intl.formatMessage(
        messages.productNote,
      )}`
      : intl.formatMessage(messages.productCode),
    searchable: true,
    sortable: true,
    width: isEdit ? 500 : 1,
    formatter: ({ data }) =>
      isEdit ? (
          <>
            <FormikAsyncSelect
              name="product"
              label=" "
              initialValue={data.product}
              onChange={product => handleOnArticleRowChange(product.value, data)}
              asyncSelectProps={{
                isClearable: true,
              }}
              loadOptions={(searchText, callback) =>
                ProductService.getOptions(searchText, callback)
              }
            />

            <FormikInput
              label=" "
              placeholder="Nota interna"
              name="note"
              initialValue={data.note}
              onChange={e => onChangeRowData(e.target.value, 'note', data.id)}
            />

            {data.product.productCode.includes("DCZ") && (
              <SimpleFormikField
                name="Descrizione DCZ"
                disabled
                initialValue={data.description}
                onChange={e =>
                  onChangeRowData(e.target.value, 'description', data.id)
                }
              />
            )}
          </>
      ) : (
          <>
            <span>{get(data, 'product.productCode', '')}</span>
            {data.isProcessed && (
              <Popup
                trigger={
                  <Icon
                    name="check circle"
                    style={{
                      fontSize: '16px',
                      marginLeft: '10px',
                      color: 'rgb(33, 133, 208)',
                    }}
                  />
                }
                inverted
                content="Articolo evaso"
                position="top center"
                style={{ borderRadius: '6px' }}
              />
            )}
            <div>{get(data, 'product.title', '')}</div>
            <div>{get(data, 'description', '')}</div>
          </>
      ),
  },
  ...(isEdit
    ? []
    : [
      {
        key: 'product.notes',
        name: intl.formatMessage(messages.productNote),
        searchable: true,
        sortable: false,
        width: 200,
        // eslint-disable-next-line react/prop-types
        formatter: ({ data }) => <NetPriceEdited>
          <span>{get(data, 'note', '')}</span>
          {data.netPriceEdited && <span>{intl.formatMessage(messages.net_price_edited_message)}</span>}
        </NetPriceEdited>,
      },
    ]),
  // Product price master
  {
    key: 'productPriceMaster',
    name: intl.formatMessage(messages.productPriceMaster),
    searchable: true,
    sortable: false,
    width: 1,
    formatter: ({ data }) => {
      if (data.product.productCode.includes("DCZ") && isEdit) { // NOTE: If the product is a DCZ, the customer price is editable
        return <>
        <div style={{display: 'flex', justifyContent: 'space-between', verticalAlign: 'middle', lineHeight: '3'}}>
          <FormikInput
            name="productMasterPrice"
            initialValue={data.productMasterPrice}
            label="Lordo"
            onChange={e => {
              onChangeRowData('0.00', 'discountValue', data.id)
              onChangeRowData('0.00', 'discountExtra', data.id)
              onChangeRowData(e.target.value, 'netPrice', data.id)
              onChangeRowData(e.target.value, 'productMasterPrice', data.id)
            }}
          />
          €
        </div>
        </>
      }
      return currencyFormatter.format(data.productMasterPrice);
    },
  },
  // Product price customer
  {
    key: 'productPriceCustomer',
    name: intl.formatMessage(messages.productPriceCustomer),
    searchable: true,
    sortable: false,
    width: 1,
    formatter: ({ data }) => currencyFormatter.format(data.productCustomerPrice),
  },
  // Price offer
  {
    key: 'offerPrice',
    name: intl.formatMessage(messages.priceOffer),
    searchable: true,
    sortable: false,
    width: 1,
    formatter: ({ data }) => {
      const offerPrice = get(data, 'offerPrice', null)
        ? `${parseFloat(data.offerPrice).toFixed(2)  } €`
        : '--';
      return offerPrice;
    },
  },
  // Net price
  {
    key: 'netPrice',
    name: intl.formatMessage(messages.netPrice),
    searchable: true,
    sortable: false,
    width: isEdit ? 100 : 1,
    // eslint-disable-next-line react/prop-types
    formatter: ({ data }) => {
      if (data.loan) return getNetPrice(data);
      if (data.goodsDiscount) return getNetPrice(data);

      return isEdit ? (
        <div style={{display: 'flex', justifyContent: 'space-between', verticalAlign: 'middle', lineHeight: '3'}}>
          <FormikInput
            name="netPrice"
            label=" "
            disabled={data.netPriceDisabled}
            initialValue={data.netPrice}
            onChange={e => {
              let np = getNetPrice(data);
              if (data.product.productCode.includes("DCZ"))
                np = data.productMasterPrice // TODO: Check if this is best option

              const value = e.target.value.replace(',', '.');
              if (
                np !== null &&
                parseFloat(np) !== 0 &&
                data.productMasterPrice !== 0
              )
                onChangeRowData(
                  parseFloat(
                    100 - (value * 100) / data.productMasterPrice,
                  ).toFixed(2),
                  'discountValue',
                  data.id,
                );

              onChangeRowData('0', 'extraDiscount', data.id);
              onChangeRowData(value, 'netPriceRow', data.id);
              onChangeRowData(
                e.target.value * parseFloat(data.quantity, 10),
                'priceAmount',
                data.id,
              );

              onChangeRowData(
                e.target.value.replace(/[^0-9.]/g, ''),
                'netPrice',
                data.id,
              );

              if (parseFloat(data.buyPrice) !== 0)
                onChangeRowData(
                  (
                    parseFloat((data.netPrice - data.buyPrice) / data.buyPrice) *
                    100
                  ).toFixed(2),
                  'markup',
                  data.id,
                );
            }}
          />
        €
        </div>
      ) : (
        currencyFormatter.format(get(data, 'netPrice', ''))
      )
    },
  },
  // Markup
  {
    key: 'markup',
    name: intl.formatMessage(messages.markup),
    searchable: true,
    sortable: false,
    width: isEdit ? 90 : 1,
    className: 'row-not-compliant',
    // eslint-disable-next-line react/prop-types
    formatter: ({ data }) => {
      if (data.loan) return <div style={{fontWeight: "bold", color: "red"}}>0%</div>;
      if (data.goodsDiscount) return <div style={{fontWeight: "bold", color: "red"}}>0%</div>;

      return isEdit ? (
        <div style={{display: 'flex', justifyContent: 'space-between', verticalAlign: 'middle', lineHeight: '3'}}>
          <FormikInput
            name="markup"
            label=" "
            initialValue={data.markup}
            onChange={e => {
              const value = e.target.value.replace(',', '.');
              const np =
                (value / 100) * parseFloat(data.buyPrice) +
                parseFloat(data.buyPrice);

              if (np !== null && parseFloat(np) !== 0 && data.buyPrice !== 0)
                onChangeRowData(value, 'markup', data.id);

              onChangeRowData(np, 'netPrice', data.id);
              onChangeRowData(
                parseFloat(100 - (np * 100) / data.productMasterPrice).toFixed(2),
                'discountValue',
                data.id,
              );
              onChangeRowData('0', 'extraDiscount', data.id);
              onChangeRowData(
                parseFloat(np * parseFloat(data.quantity, 10)).toFixed(2),
                'priceAmount',
                data.id,
              );
            }}
          />
          %
        </div>
      ) : (
          <>
            {(() => {
              let markup = get(data, 'markup', '');
              markup = data.loan ? "0" : markup;
              const isRed = parseFloat(markup) <= 34.99

              return (
                <span style={{ color: isRed ? 'red' : 'green' }}>
                  {`${markup}%`}
                </span>
              );
            })()}
          </>
      )
    },
  },
  // Quantity
  {
    key: 'quantity',
    name: intl.formatMessage(messages.qt),
    searchable: true,
    sortable: false,
    width: isEdit ? 70 : 1,
    // eslint-disable-next-line react/prop-types
    formatter: ({ data }) =>
      isEdit ? (
        <FormikInput
          name="quantity"
          label=" "
          initialValue={data.quantity}
          onChange={e => {
            onChangeRowData(e.target.value, 'quantity', data.id);
            onChangeRowData(
              data.netPrice * parseFloat(data.quantity, 10),
              'priceAmount',
              data.id,
            );
          }}
        />
      ) : (
        get(data, 'quantity', '')
      ),
  },
  // Discount value
  // {
  //   key: 'maxDiscount',
  //   width: 110,
  //   name: "Sc. Max",
  //   className: "row-not-compliant",
  //   // eslint-disable-next-line react/prop-types
  //   formatter: ({ data }) => data.product.maxDiscount,
  // },
  {
    key: 'discountValue',
    name: intl.formatMessage(messages.discountValue),
    searchable: true,
    sortable: false,
    width: isEdit ? 90 : 1,
    className: 'row-not-compliant',
    formatter: ({ data }) => {
      if (data.loan) return <span style={{fontWeight: "bold", color: "red"}}>100%</span>;
      if (data.goodsDiscount) return <span style={{fontWeight: "bold", color: "red"}}>100%</span>;

      return isEdit ? (
          <>
            <div style={{display: 'flex', justifyContent: 'space-between', verticalAlign: 'middle', lineHeight: '3'}}>
              <FormikInput
                name="discountValue"
                label=" "
                disabled={data.discountDisabled || false || data.loan}
                placeholder={intl.formatMessage(messages.discountValue)}
                initialValue={data.discountValue}
                onChange={e => {
                  let basePrice = getNetPrice(data);
                  if (data.product.productCode.includes("DCZ"))
                    basePrice = data.productMasterPrice // TODO: Check if this is best option

                  let discountExtra = data.extraDiscount;

                  const totalDiscount =
                  parseFloat(e.target.value) + parseFloat(discountExtra);
                  if (totalDiscount !== 0) basePrice = data.productMasterPrice;

                  const value = parseFloat(e.target.value.replace(',', '.'), 10);
                  const discount = value || 0;
                  discountExtra = parseFloat(discountExtra) || 0;
                  if (discountExtra !== '') {
                    const discountedBase = (basePrice / 100) * (100 - discount);
                    onChangeRowData(
                      parseFloat(
                        (discountedBase / 100) * (100 - discountExtra),
                      ).toFixed(2),
                      'netPrice',
                      data.id,
                    );
                  }

                  onChangeRowData(
                    data.netPrice * parseFloat(data.minQuantity, 10),
                    'amount',
                    data.id,
                  );

                  if (parseFloat(data.buyPrice) !== 0)
                    onChangeRowData(
                      (
                        parseFloat(
                          (data.netPrice - data.buyPrice) / data.buyPrice,
                        ) * 100
                      ).toFixed(2),
                      'markup',
                      data.id,
                    );

                  if (e.target.value !== '')
                    onChangeRowData(e.target.value, 'discountValue', data.id);
                  else onChangeRowData('0.00', 'discountValue', data.id);
                }}
              />
              %
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', verticalAlign: 'middle', lineHeight: '3'}}>
              <FormikInput
                name="extraDiscount"
                label=" "
                disabled={data.discountExtraDisabled || false || data.loan}
                placeholder={intl.formatMessage(messages.extraDiscount)}
                initialValue={data.extraDiscount}
                onChange={e => {
                  let basePrice = getNetPrice(data);
                  if (data.product.productCode.includes("DCZ"))
                    basePrice = data.productMasterPrice // TODO: Check if this is best option

                  let discount = data.discountValue;

                  // NOTE: If any discount is applied use productMasterPrice as base
                  if (parseFloat(discount) + parseFloat(e.target.value) !== 0)
                    basePrice = data.productMasterPrice;

                  const value = parseFloat(e.target.value, 10);
                  const discountExtra = value || 0;
                  discount = parseFloat(discount) || 0;
                  if (discountExtra !== '') {
                    const discountedBase = (basePrice / 100) * (100 - discount);
                    onChangeRowData(
                      parseFloat(
                        (discountedBase / 100) * (100 - discountExtra),
                      ).toFixed(2),
                      'netPrice',
                      data.id,
                    );
                  }

                  onChangeRowData(
                    data.netPrice * parseFloat(data.minQuantity, 10),
                    'amount',
                    data.id,
                  );

                  if (parseFloat(data.buyPrice) !== 0)
                    onChangeRowData(
                      (
                        parseFloat(
                          (data.netPrice - data.buyPrice) / data.buyPrice,
                        ) * 100
                      ).toFixed(2),
                      'markup',
                      data.id,
                    );

                  if (e.target.value !== '')
                    onChangeRowData(e.target.value, 'extraDiscount', data.id);
                  else onChangeRowData('0.00', 'extraDiscount', data.id);
                }}
              />
              %
            </div>
          </>
      ) : (
          <>
            {(() => {
              let color = "black";
              const discountValue = parseFloat(get(data, 'discountValue', '0.00'));
              const extraDiscount = parseFloat(get(data, 'extraDiscount', '0.00'));

              if (discountValue + extraDiscount > 30.99)
                color = "red";

              if (data.netPrice === data.productCustomerPrice)
                color = "black";

              return (
                <span style={{ color }}>
                  {`${discountValue}%`}
                  <br />
                  {`${extraDiscount}%`}
                </span>
              );
            })()}
          </>
      )
    },
  },
  // Price amount
  {
    key: 'priceAmount',
    name: intl.formatMessage(messages.priceAmount),
    searchable: true,
    sortable: false,
    width: 1,
    formatter: ({ data }) => currencyFormatter.format(get(data, 'priceAmount', '')),
  },
  // Goods discount
  {
    key: 'goodsDiscount',
    name: intl.formatMessage(messages.goodsDiscount),
    searchable: true,
    sortable: false,
    width: 1,
    // eslint-disable-next-line react/prop-types
    formatter: ({ data }) =>
      isEdit ? (
        <Checkbox
          name="goodsDiscount"
          label=" "
          checked={data.goodsDiscount}
          onChange={(value, toggleData) => {
            applyMerchDiscount(
              toggleData.checked,
              data,
              onChangeRowData,
              getNetPrice,
            );
            onChangeRowData(toggleData.checked, 'goodsDiscount', data.id);
            onChangeRowData(false, 'loan', data.id);
          }}
        />
      ) : (
          <>
            <div style={{ fontWeight: data.goodsDiscount ? 'bold' : 'normal' }}>
              {data.goodsDiscount
                ? intl.formatMessage(messages.yes)
                : intl.formatMessage(messages.no)}
            </div>
          </>
      ),
  },
  // Loan
  {
    key: 'loan',
    name: intl.formatMessage(messages.loan),
    searchable: true,
    sortable: false,
    width: 1,
    formatter: ({ data }) =>
      isEdit ? (
          <>
            <Checkbox
              name="loan"
              label=" "
              checked={data.loan}
              disabled={!data.product.sellLoan}
              onChange={(value, toggleData) => {
                applyMerchDiscount(
                  toggleData.checked,
                  data,
                  onChangeRowData,
                  getNetPrice,
                );
                onChangeRowData(toggleData.checked, 'loan', data.id);
                onChangeRowData(false, 'goodsDiscount', data.id);
              }}
            />
          </>
      ) : (
          <>
            <div style={{ fontWeight: data.loan ? 'bold' : 'normal' }}>
              {data.loan
                ? intl.formatMessage(messages.yes)
                : intl.formatMessage(messages.no)}
            </div>
          </>
      ),
  },
  {
    key: 'netPriceEdited',
    width: 40,
    name: <span>SF</span>,
    formatter: ({ data }) =>
      isEdit ? (
          <>
            <Checkbox
              name="netPriceEdited"
              label=" "
              checked={data.netPriceEdited}
              onChange={(value, toggleData) => {
                onChangeRowData(toggleData.checked, 'netPriceEdited', data.id);
              }}
            />
          </>) : (
          <>
            <div style={{ fontWeight: data.netPriceEdited ? 'bold' : 'normal' }}>
              {data.netPriceEdited
                ? intl.formatMessage(messages.yes)
                : intl.formatMessage(messages.no)}
            </div>
          </>
      ),
  },

  // Actions
  ...(!isEdit
    ? []
    : [
      {
        key: 'actions',
        name: intl.formatMessage(messages.actions),
        width: 70,
        searchable: true,
        sortable: false,
        formatter: ({ data }) => OrderRowActions(onAction, data),
      },
    ]),
];

export const OrderActions = (manageAction, data) => {
  const canView = PermissionManager.CanI('read', 'order');
  const canEdit =
    PermissionManager.CanI('edit', 'order') &&
    data.state !== ORDER_STATE_ACCEPTED &&
    !data.imported;
  // const state = data ? data.state : '';
  // const canApprove =
  //   PermissionManager.CanI('statusUpdateApproved', 'custom') &&
  //   state !== ORDER_STATE_ACCEPTED &&
  //   !data.imported;
  // const canUnapprove =
  //   PermissionManager.CanI('statusUpdateApproved', 'custom') &&
  //   ![ORDER_STATE_NOT_ACCEPTED, ORDER_STATE_ACCEPTED].includes(state) &&
  //   !data.imported;

  return (
    <div>
      {/* Show button */}
      {canView ? (
        <span className="order-action">
          <Popup
            basic
            content={<FormattedMessage {...appMessages.action_view} />}
            trigger={
              <Button
                circular
                color="blue"
                icon="eye"
                onClick={() => manageAction('view', data)}
              />
            }
          />
        </span>
      ) : null}
      {/* Edit button */}
      {canEdit ? (
        <span className="order-action">
          <Popup
            basic
            content={<FormattedMessage {...appMessages.action_modify} />}
            trigger={
              <Button
                circular
                color="blue"
                icon="edit"
                onClick={() => manageAction('modify', data)}
              />
            }
          />
        </span>
      ) : null}
      {/* Delete button */}
      {canEdit ? (
        <span className="order-action">
          <Popup
            basic
            content={<FormattedMessage {...appMessages.action_delete} />}
            trigger={
              <Button
                circular
                color="red"
                icon="trash"
                onClick={() => manageAction('delete', data)}
              />
            }
          />
        </span>
      ) : null}
      {/* <ButtonDropdownCollapse
        key="collapse-button"
        dropdownItems={[
          <Dropdown.Item
            style={{ color: 'red' }}
            key={<FormattedMessage {...appMessages.action_delete} />}
            onClick={() => manageAction('delete', data)}
          >
            <Icon name="trash" />
            <FormattedMessage {...appMessages.action_delete} />
          </Dropdown.Item>,
          ...(canApprove
            ? [
                <Dropdown.Item
                  style={{ color: 'green' }}
                  key={<FormattedMessage {...appMessages.action_approve} />}
                  onClick={() => manageAction('approve', data)}
                >
                  <Icon name="checkmark" />
                  <FormattedMessage {...appMessages.action_approve} />
                </Dropdown.Item>,
              ]
            : []),
          ...(canUnapprove
            ? [
                <Dropdown.Item
                  style={{ color: 'orange' }}
                  key={<FormattedMessage {...appMessages.action_not_approve} />}
                  onClick={() => manageAction('unapprove', data)}
                >
                  <Icon name="ban" />
                  <FormattedMessage {...appMessages.action_not_approve} />
                </Dropdown.Item>,
              ]
            : []),
        ]}
        isGrouped={false}
      /> */}
    </div>
  );
};

export const OrderRowActions = (onAction, data) => {
  const canDelete = PermissionManager.CanI('remove', 'orderRow');

  return (
    <>
      {/* Delete button */}
      {canDelete ? (
        <span className="order-action">
          <RemoveBtn
            action={() => onAction('delete', data)}
            notice
            icon="trash"
          />
        </span>
      ) : null}
    </>
  );
};

/**
 * Returns the list of order filters.
 * @param {*} intl
 * @returns
 */
export const OrderFilters = intl => ({
  active: true,
  title: intl.formatMessage(appMessages.search),
  buttonLabel: intl.formatMessage(appMessages.search),
  formStructure: (
    <>
      <Grid.Column>
        <SimpleFormikField
          name="number"
          label={intl.formatMessage(messages.orderNumber)}
        />
      </Grid.Column>
      <Grid.Column>
        <FormikAsyncSelect
          name="destination"
          label={intl.formatMessage(messages.customer)}
          loadOptions={(searchText, callback) =>
            DestinationService.getOptions(searchText, callback, true)
          }
        />
      </Grid.Column>
      <Grid.Column width={2}>
        <FormikDate
          name="createdAtFrom"
          label={intl.formatMessage(messages.start)}
        />
      </Grid.Column>
      <Grid.Column width={2}>
        <FormikDate
          name="createdAtTo"
          label={intl.formatMessage(messages.end)}
        />
      </Grid.Column>
      <PaddedColumn>
        <FormikAsyncSelect
          name="state"
          label={intl.formatMessage(messages.state)}
          loadOptions={(searchText, callback) =>
            OrderService.getStateOptions(callback)
          }
        />
      </PaddedColumn>

      <PaddedColumn>
        <FormikAsyncSelect
          name="agent"
          label={intl.formatMessage(messages.agent)}
          loadOptions={(searchText, callback) =>
            AgentService.getOptions(searchText, callback)
          }
        />
      </PaddedColumn>
      <PaddedColumn>
        <FormikAsyncSelect
          name="orderType"
          label={intl.formatMessage(messages.orderType)}
          loadOptions={(searchText, callback) =>
            OrderTypologyService.getOptions(searchText, callback)
          }
        />
      </PaddedColumn>
    </>
  ),
  onApplyFilter: (values, formikActionsBag, applyfilterFunction) => {
    const filters = [];
    const filterLabels = [];

    if (values.number) {
      filterLabels.push({
        key: 'number',
        name: intl.formatMessage(messages.orderNumber),
        value: values.number,
        filterField: 'number',
      });
      filters.push({
        type: 'like',
        where: 'and',
        field: 'number',
        value: `%${values.number}%`,
      });
    }

    if (values.destination) {
      filterLabels.push({
        key: 'destination',
        name: intl.formatMessage(messages.destination),
        value: values.destination.label,
        filterField: 'id',
      });
      filters.push({
        type: 'like',
        where: 'and',
        alias: 'destination',
        field: 'id',
        value: `%${values.destination.value}%`,
      });
    }

    if (values.createdAtFrom) {
      filterLabels.push({
        key: 'createdAtFrom',
        name: intl.formatMessage(messages.start),
        value: `${moment(values.createdAtFrom).format('YYYY-MM-DD')} `,
        filterField: 'createdAtFrom',
      });
      filters.push({
        type: 'gte',
        where: 'and',
        field: 'createdAt',
        value: `${moment(values.createdAtFrom).format('YYYY-MM-DD')} 00:00:00`,
        format: 'Y-m-d H:i:s',
      });
    }

    if (values.createdAtTo) {
      filterLabels.push({
        key: 'createdAtTo',
        name: intl.formatMessage(messages.end),
        value: `${moment(values.createdAtTo).format('YYYY-MM-DD')}`,
        filterField: 'referenceDate',
      });
      filters.push({
        type: 'lte',
        where: 'and',
        field: 'createdAt',
        value: `${moment(values.createdAtTo).format('YYYY-MM-DD')} 23:59:59`,
        format: 'Y-m-d H:i:s',
      });
    }
    if (values.state) {
      filterLabels.push({
        key: 'state',
        name: intl.formatMessage(messages.state),
        value: values.state.label,
        filterField: 'state',
      });
      if (values.state.value) {
        filters.push({
          type: 'eq',
          where: 'and',
          field: 'state',
          value: `${values.state.value}`,
        });
      } else {
        filters.push({
          type: 'isnotnull',
          where: 'and',
          field: 'state',
        });
      }
    }
    if (values.agent) {
      filterLabels.push({
        key: 'agent',
        name: intl.formatMessage(messages.agent),
        value: values.agent.label,
        filterField: 'agent1Name',
      });
      filters.push({
        type: 'eq',
        where: 'and',
        field: 'agent1',
        alias: 'destination',
        value: `${values.agent.value}`,
      });
    }

    if (values.orderType) {
      filterLabels.push({
        key: 'orderType',
        name: intl.formatMessage(messages.orderType),
        value: values.orderType.label,
        filterField: 'id',
      });
      filters.push({
        type: 'eq',
        where: 'and',
        field: 'id',
        alias: 'orderType',
        value: `${values.orderType.value}`,
      });
    }

    applyfilterFunction(filters, filterLabels, formikActionsBag);
  },
});
